(() => {
  const dialog = document.getElementById('search-drawer');
  const configNode = document.getElementById('bs-search-config');
  if (!dialog || !configNode || dialog.dataset.bsReady) return;
  dialog.dataset.bsReady = 'true';
  const config = JSON.parse(configNode.textContent);
  const input = dialog.querySelector('#bs-search-query');
  const results = dialog.querySelector('[data-bs-search-results]');
  const idle = dialog.querySelector('[data-bs-search-idle]');
  const feedback = dialog.querySelector('[data-bs-search-feedback]');
  const message = dialog.querySelector('[data-bs-search-message]');
  const fallback = dialog.querySelector('[data-bs-search-fallback]');
  const status = dialog.querySelector('[data-bs-search-status]');
  const clearButton = dialog.querySelector('[data-bs-search-clear]');
  const body = dialog.querySelector('[data-bs-search-body]');
  const cache = new Map();
  let pending = null;
  let timer;
  let generation = 0;
  let trigger = null;
  let restoring = false;
  let closing = false;
  let previousOverflow = '';
  let mobileScrollLock = null;
  let viewportFrame = 0;
  const mobileViewport = matchMedia('(max-width: 699px)');
  const headerTriggers = [...document.querySelectorAll('[data-bs-search-trigger]')].map(form => {
    const field = form.querySelector('input[name="q"]');
    const button = form.querySelector('.search-input button');
    return { form, field, button, attributes: [
      ...['readonly', 'inputmode', 'tabindex', 'aria-hidden'].map(name => [field, name, field.getAttribute(name)]),
      ...['type', 'data-bs-search-open', 'aria-haspopup', 'aria-controls'].map(name => [button, name, button.getAttribute(name)])
    ] };
  });
  const configureHeaderTriggers = () => {
    for (const { form, field, button, attributes } of headerTriggers) {
      form.toggleAttribute('data-bs-search-launcher', mobileViewport.matches);
      if (mobileViewport.matches) {
        field.readOnly = true;
        field.inputMode = 'none';
        field.tabIndex = -1;
        field.setAttribute('aria-hidden', 'true');
        button.type = 'button';
        button.setAttribute('data-bs-search-open', '');
        button.setAttribute('aria-haspopup', 'dialog');
        button.setAttribute('aria-controls', 'search-drawer');
      } else {
        for (const [element, name, value] of attributes) {
          if (value === null) element.removeAttribute(name);
          else element.setAttribute(name, value);
        }
      }
    }
  };
  const normalize = value => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/sponge\s+bob/g, 'spongebob').replace(/spider\s+man/g, 'spiderman').replace(/[^a-z0-9]+/g, ' ').trim().replace(/\s+/g, ' ');
  const categoryKey = value => normalize(value.split(/\s[–—|]\s|,\s/)[0]).replace(/\bhelium gevulde?\s+/g, '').replace(/\s*\bballon(?:nen)?(?:\s+(?:versturen|bezorgen|bestellen|kopen))?$/, '').trim();
  const searchURL = query => {
    const url = new URL(config.searchUrl, location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('type', 'product');
    url.searchParams.set('options[prefix]', 'last');
    return url.pathname + url.search;
  };
  const safeCollectionURL = value => {
    try {
      const url = new URL(value, location.origin);
      if (url.origin === location.origin && /\/collections\/[^/]+/.test(url.pathname)) return url.pathname;
    } catch (_) {}
    return null;
  };
  const exactCollection = (query, categories) => {
    const key = normalize(query).replace(/\bhelium gevulde?\s+/g, '').replace(/\s*\bballon(?:nen)?(?:\s+(?:versturen|bezorgen|bestellen|kopen))?$/, '').trim();
    if (!key) return null;
    const matches = categories.filter(category => categoryKey(category.title) === key).map(category => safeCollectionURL(category.url)).filter(Boolean);
    const unique = [...new Set(matches)];
    return unique.length === 1 ? unique[0] : null;
  };
  const rankCollections = (entries, query) => {
    const key = categoryKey(query);
    const words = key.split(' ').filter(word => word && !['ballon', 'ballonnen', 'helium', 'versturen', 'bezorgen', 'bestellen', 'kopen'].includes(word));
    const numbers = new Set((normalize(query).match(/\d+/g) || []).map(Number));
    return entries.map((entry, index) => {
      const title = categoryKey(entry.title);
      const age = title.match(/\b(\d{1,3})\s*(?:jaar|jarig|jarige)\b/);
      if (age && !numbers.has(Number(age[1]))) return null;
      let score = words.filter(word => title.split(' ').some(part => part.startsWith(word))).length * 10;
      if (age) score += 40;
      if (key && title === key) score += 100;
      else if (key && title.startsWith(key)) score += 75;
      else if (key && title.includes(key)) score += 60;
      return { entry, score, index };
    }).filter(Boolean).sort((a, b) => b.score - a.score || a.index - b.index).slice(0, 5).map(result => result.entry);
  };
  const orderCollectionSuggestions = (content, query) => {
    const section = content.querySelector('.bs-search-collection-results');
    if (!section) return;
    const list = section.querySelector('.bs-search-categories');
    const entries = [...list.querySelectorAll('[data-bs-search-collection]')].map(link => ({ title: link.dataset.title, node: link.closest('li') }));
    const ranked = rankCollections(entries, query);
    if (ranked.length) list.replaceChildren(...ranked.map(entry => entry.node));
    else section.remove();
  };
  const cached = query => {
    const key = normalize(query);
    const entry = cache.get(key);
    if (entry && Date.now() - entry.time < 120000) return entry.payload;
    cache.delete(key);
    return null;
  };
  let catalogPromise = null;
  let catalogReady = null;
  let catalogRetryAt = 0;
  const loadCatalog = () => {
    if (!config.catalogUrl || !config.rankingUrl || Date.now() < catalogRetryAt) return Promise.resolve(null);
    if (catalogPromise) return catalogPromise;
    const controller = new AbortController();
    let timeout;
    const deadline = new Promise((_, reject) => { timeout = setTimeout(() => { controller.abort(); reject(new Error('Ranking timeout')); }, 1800); });
    catalogPromise = Promise.race([deadline, Promise.all([
      import(config.rankingUrl),
      fetch(config.catalogUrl, { signal: controller.signal }).then(response => {
        if (!response.ok) throw new Error('Ranking unavailable');
        return response.json();
      })
    ])]).then(([ranking, data]) => {
      const catalog = ranking.prepareCatalog(data);
      const root = window.Shopify?.routes.root || '/';
      const routes = ranking.prepareCollectionRoutes([
        ...catalog.collections.map(category => ({ title: category.title, url: `${root}collections/${category.handle}` })),
        ...config.categories, ...(config.aliases || [])
      ]);
      catalogReady = {
        search: query => ranking.searchCatalog(catalog, query),
        resolve: query => safeCollectionURL(ranking.resolveCollection(routes, query)),
        through: data.through
      };
      return catalogReady;
    }).catch(() => {
      catalogRetryAt = Date.now() + 60000;
      catalogPromise = null;
      return null;
    }).finally(() => clearTimeout(timeout));
    return catalogPromise;
  };
  const fetchRankedCards = async (products, signal) => {
    const controller = new AbortController();
    const abort = () => controller.abort();
    signal?.addEventListener('abort', abort, { once: true });
    if (signal?.aborted) controller.abort();
    const timeout = setTimeout(abort, 3000);
    try {
      const url = new URL(config.searchUrl, location.origin);
      url.searchParams.set('section_id', 'bs-search-ranked-products');
      url.searchParams.set('q', products.map(product => product.handle).join('|'));
      url.searchParams.set('type', 'product');
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) throw new Error('Product cards unavailable');
      const content = new DOMParser().parseFromString(await response.text(), 'text/html').querySelector('[data-bs-ranked-products]');
      if (!content || !content.querySelector('.rao-product-card') || content.textContent.includes('Liquid error')) throw new Error('Missing product cards');
      content.querySelectorAll('script').forEach(script => script.remove());
      return content;
    } finally {
      clearTimeout(timeout);
      signal?.removeEventListener('abort', abort);
    }
  };
  const nativeResults = async (query, signal, types = 'collection,product') => {
    const url = new URL(config.suggestUrl || (window.Shopify?.routes.root || '/') + 'search/suggest', location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('section_id', 'bs-search-results');
    url.searchParams.set('resources[type]', types);
    url.searchParams.set('resources[limit]', '8');
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[options][unavailable_products]', 'last');
    const response = await fetch(url, { signal, headers: { 'X-Requested-With': 'XMLHttpRequest' } });
    if (!response.ok) throw new Error('Search unavailable');
    const content = new DOMParser().parseFromString(await response.text(), 'text/html').querySelector('.bs-search-response');
    if (!content) throw new Error('Missing search results');
    content.querySelectorAll('script').forEach(script => script.remove());
    return content;
  };
  const fetchResults = query => {
    const hit = cached(query);
    if (hit) return Promise.resolve(hit);
    const key = normalize(query);
    if (pending?.key === key && !pending.controller.signal.aborted) return pending.promise;
    pending?.controller.abort();
    const controller = new AbortController();
    const promise = (async () => {
      const readyMatches = catalogReady?.search(query);
      const types = readyMatches?.length ? 'collection' : 'collection,product';
      // Start Shopify suggestions immediately, even on the first search or a slow connection.
      const native = nativeResults(query, controller.signal, types).then(content => ({ content }), error => ({ error }));
      const catalog = await loadCatalog();
      if (controller.signal.aborted) throw new DOMException('Aborted', 'AbortError');
      const matches = readyMatches || catalog?.search(query);
      let ranked = null;
      if (matches?.length) {
        try { ranked = await fetchRankedCards(matches.slice(0, 8), controller.signal); }
        catch (error) { if (controller.signal.aborted) throw error; }
      }
      const result = await native;
      if (result.error) throw result.error;
      let content = result.content;
      if (ranked) {
        const section = content.querySelector('.bs-search-product-results');
        section.querySelector('.bs-search-grid').replaceWith(ranked);
        section.hidden = false;
        content.querySelector('.bs-search-empty')?.remove();
        content.dataset.productCount = ranked.querySelectorAll('.rao-product-card').length;
        content.dataset.rankedThrough = catalog.through;
      } else if (types === 'collection') {
        content = await nativeResults(query, controller.signal);
      }
      orderCollectionSuggestions(content, query);
      const payload = {
        html: content.outerHTML,
        count: Number(content.dataset.productCount) || 0,
        categories: [...content.querySelectorAll('[data-bs-search-collection]')].map(link => ({ title: link.dataset.title, url: link.getAttribute('href') }))
      };
      cache.set(key, { time: Date.now(), payload });
      if (cache.size > 20) cache.delete(cache.keys().next().value);
      return payload;
    })().finally(() => { if (pending?.controller === controller) pending = null; });
    pending = { key, controller, promise };
    return promise;
  };
  const setIdle = () => {
    dialog.style.minHeight = '';
    idle.hidden = false;
    results.hidden = true;
    feedback.hidden = true;
    results.removeAttribute('aria-busy');
    status.textContent = '';
  };
  const showResults = async (query, version) => {
    status.textContent = config.loading;
    try {
      const payload = await fetchResults(query);
      if (version !== generation || !dialog.open) return;
      dialog.style.minHeight = '';
      results.innerHTML = payload.html;
      results.hidden = false;
      feedback.hidden = true;
      results.removeAttribute('aria-busy');
      status.textContent = config.ready.replace('[count]', String(payload.count));
    } catch (error) {
      if (error.name === 'AbortError' || version !== generation || !dialog.open) return;
      dialog.style.minHeight = '';
      results.hidden = true;
      results.removeAttribute('aria-busy');
      message.textContent = config.error;
      fallback.href = searchURL(query);
      fallback.hidden = false;
      feedback.hidden = false;
      status.textContent = config.error;
    }
  };
  const onInput = () => {
    if (!dialog.open) return;
    clearTimeout(timer);
    const version = ++generation;
    const query = input.value.trim();
    clearButton.hidden = !query;
    pending?.controller.abort();
    if (query.length < 2) { setIdle(); return; }
    if (matchMedia('(min-width: 700px)').matches) dialog.style.minHeight = `min(${dialog.getBoundingClientRect().height}px, calc(100dvh - 48px))`;
    idle.hidden = true;
    results.hidden = true;
    results.setAttribute('aria-busy', 'true');
    feedback.hidden = false;
    fallback.hidden = true;
    message.textContent = config.loading;
    timer = setTimeout(() => showResults(query, version), cached(query) ? 0 : 160);
  };
  const lockMobileScroll = () => {
    if (mobileScrollLock || !matchMedia('(max-width: 699px)').matches) return;
    const style = document.body.style;
    const properties = ['position', 'top', 'left', 'width', 'overflow'];
    mobileScrollLock = {
      x: scrollX, y: scrollY,
      styles: properties.map(property => [property, style.getPropertyValue(property), style.getPropertyPriority(property)])
    };
    style.setProperty('position', 'fixed');
    style.setProperty('top', -mobileScrollLock.y + 'px');
    style.setProperty('left', -mobileScrollLock.x + 'px');
    style.setProperty('width', '100%');
    style.setProperty('overflow', 'hidden');
  };
  const unlockMobileScroll = () => {
    if (!mobileScrollLock) return false;
    const saved = mobileScrollLock;
    mobileScrollLock = null;
    for (const [property, value, priority] of saved.styles) {
      if (value) document.body.style.setProperty(property, value, priority);
      else document.body.style.removeProperty(property);
    }
    window.scrollTo({ left: saved.x, top: saved.y, behavior: 'instant' });
    return true;
  };
  const updateViewport = () => {
    viewportFrame = 0;
    if (!dialog.open) return;
    const viewport = window.visualViewport;
    const height = viewport?.height || innerHeight;
    const layoutHeight = Math.max(innerHeight, document.documentElement.clientHeight);
    const top = Math.min(Math.max(0, viewport?.offsetTop || 0), Math.max(0, layoutHeight - height));
    dialog.style.setProperty('--bs-search-height', height + 'px');
    dialog.style.setProperty('--bs-search-top', top + 'px');
  };
  const scheduleViewport = () => {
    if (dialog.open && !viewportFrame) viewportFrame = requestAnimationFrame(updateViewport);
  };
  const open = (source, query = '') => {
    if (dialog.open || closing) return;
    loadCatalog();
    trigger = mobileViewport.matches && source?.matches('input') ? source.closest('form')?.querySelector('.search-input button') || source : source;
    if (mobileViewport.matches) trigger?.focus({ preventScroll: true });
    input.readOnly = false;
    previousOverflow = document.documentElement.style.overflow;
    lockMobileScroll();
    document.documentElement.style.overflow = 'hidden';
    input.value = query;
    dialog.showModal();
    updateViewport();
    input.focus({ preventScroll: true });
    body.scrollTop = 0;
    onInput();
  };
  const endTextEntry = () => {
    closing = true;
    restoring = true;
    input.blur();
    input.readOnly = true;
    for (const { field } of headerTriggers) field.blur();
  };
  dialog.addEventListener('beforetoggle', event => {
    if (event.newState === 'closed') endTextEntry();
  });
  dialog.addEventListener('close', () => {
    endTextEntry();
    clearTimeout(timer);
    ++generation;
    pending?.controller.abort();
    cancelAnimationFrame(viewportFrame);
    viewportFrame = 0;
    document.documentElement.style.overflow = previousOverflow;
    const wasMobile = unlockMobileScroll();
    dialog.style.minHeight = '';
    restoring = true;
    const returnTarget = wasMobile && trigger?.matches('input') ? trigger.closest('form')?.querySelector('.search-input button') : trigger;
    returnTarget?.focus({ preventScroll: true });
    closing = false;
    setTimeout(() => { restoring = false; }, 0);
  });
  const close = () => { if (dialog.open) { endTextEntry(); dialog.close(); } };
  dialog.addEventListener('cancel', event => { event.preventDefault(); close(); });
  dialog.querySelector('[data-bs-search-close]').addEventListener('click', close);
  dialog.addEventListener('click', event => {
    const link = event.target.closest('a[href]');
    if (link && !event.defaultPrevented && !event.target.closest('button') && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && event.button === 0 && (!link.target || link.target === '_self')) {
      close();
      return;
    }
    if (event.target !== dialog) return;
    const rect = dialog.getBoundingClientRect();
    if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) close();
  });
  clearButton.addEventListener('click', () => { input.value = ''; input.focus(); onInput(); });
  input.addEventListener('input', event => { if (!event.isComposing) onInput(); });
  input.addEventListener('compositionend', onInput);
  dialog.addEventListener('keydown', event => {
    if (!['ArrowDown', 'ArrowUp'].includes(event.key)) return;
    const links = [...dialog.querySelectorAll((results.hidden ? '[data-bs-search-idle]' : '[data-bs-search-results]') + ' .bs-search-categories a')];
    if (event.target === input && event.key === 'ArrowDown' && links.length) { event.preventDefault(); links[0].focus(); }
    else if (links.includes(event.target)) {
      event.preventDefault();
      const index = links.indexOf(event.target) + (event.key === 'ArrowDown' ? 1 : -1);
      (index < 0 ? input : links[Math.min(index, links.length - 1)]).focus();
    }
  });
  document.addEventListener('focusin', event => {
    if (restoring) return;
    const field = event.target.closest('[data-bs-search-trigger] input[name="q"]');
    if (field && !field.closest('[data-bs-search-launcher]')) open(field, field.value);
  });
  document.addEventListener('click', event => {
    const field = event.target.closest('[data-bs-search-trigger] input[name="q"]');
    if (field && !restoring) open(field, field.value);
    const button = event.target.closest('[data-bs-search-open]');
    if (button) { event.preventDefault(); open(button, button.dataset.query || ''); }
  });
  document.addEventListener('submit', async event => {
    const form = event.target;
    if (!form.matches('[data-bs-search-form], [data-bs-search-trigger]')) return;
    event.preventDefault();
    const query = form.querySelector('[name="q"]').value.trim();
    if (!query) { if (!dialog.open) open(form.querySelector('[name="q"]')); return; }
    const version = ++generation;
    clearTimeout(timer);
    const direct = exactCollection(query, [...config.categories, ...(config.aliases || [])]) || catalogReady?.resolve(query);
    if (direct) { close(); location.assign(direct); return; }
    let destination = null;
    try {
      destination = await Promise.race([
        loadCatalog().then(async catalog => {
          if (catalog) return catalog.resolve(query);
          const payload = await fetchResults(query);
          return exactCollection(query, payload.categories);
        }),
        new Promise(resolve => setTimeout(() => resolve(null), 600))
      ]);
    } catch (_) {}
    if (version === generation) { close(); location.assign(destination || searchURL(query)); }
  });
  const searchPage = document.querySelector('[data-bs-search-page]');
  if (searchPage) {
    const target = searchPage.querySelector('[data-bs-search-page-results]');
    const restoreNative = () => {
      if (searchPage.dataset.bsResolved) return;
      target.replaceChildren(searchPage.querySelector('[data-bs-search-native]').content.cloneNode(true));
      target.removeAttribute('aria-busy');
      searchPage.dataset.bsResolved = 'native';
    };
    const pagination = (page, total, query) => {
      const nav = document.createElement('nav');
      nav.className = 'pagination';
      nav.setAttribute('aria-label', config.pagination);
      const item = (next, reverse) => {
        const enabled = next > 0 && next <= total;
        const link = document.createElement(enabled ? 'a' : 'span');
        link.className = 'pagination__item ' + (enabled ? 'group' : 'pagination__item--disabled');
        if (enabled) {
          const url = new URL(searchURL(query), location.origin);
          url.searchParams.set('page', next);
          link.href = url.pathname + url.search;
          link.rel = reverse ? 'prev' : 'next';
          link.setAttribute('aria-label', config.goToPage.replace('[index]', next));
        }
        const arrow = document.createElement('span');
        arrow.className = 'animated-arrow' + (reverse ? ' animated-arrow--reverse' : '');
        link.append(arrow);
        return link;
      };
      const current = document.createElement('span');
      current.className = 'pagination__current';
      current.textContent = `${page}  /  ${total}`;
      nav.append(item(page - 1, true), current, item(page + 1, false));
      return nav;
    };
    (async () => {
      try {
        const query = searchPage.dataset.query;
        const direct = exactCollection(query, [...config.categories, ...(config.aliases || [])]);
        if (direct) { location.replace(direct); return; }
        const catalog = await loadCatalog();
        const destination = catalog?.resolve(query);
        if (destination) { location.replace(destination); return; }
        const matches = catalog?.search(query);
        if (!matches?.length || searchPage.dataset.bsResolved) { restoreNative(); return; }
        const total = Math.ceil(matches.length / 20);
        const requestedPage = Math.max(1, Math.floor(Number(new URL(location.href).searchParams.get('page')) || 1));
        const page = Math.min(requestedPage, total);
        const cards = await fetchRankedCards(matches.slice((page - 1) * 20, page * 20));
        if (searchPage.dataset.bsResolved) return;
        target.replaceChildren(cards);
        if (total > 1) target.append(pagination(page, total, query));
        const count = searchPage.querySelector('[data-bs-search-count]');
        count.textContent = matches.length === 1 ? config.countOne : config.countMany.replace('[count]', matches.length);
        count.hidden = false;
        target.removeAttribute('aria-busy');
        searchPage.dataset.bsResolved = 'ranked';
        searchPage.dataset.rankedThrough = catalog.through;
      } catch (_) { restoreNative(); }
    })();
  }
  window.visualViewport?.addEventListener('resize', scheduleViewport, { passive: true });
  window.visualViewport?.addEventListener('scroll', scheduleViewport, { passive: true });
  window.addEventListener('resize', scheduleViewport, { passive: true });
  mobileViewport.addEventListener('change', configureHeaderTriggers);
  configureHeaderTriggers();
  window.addEventListener('pagehide', close);
  window.addEventListener('pageshow', () => { configureHeaderTriggers(); if (dialog.open) close(); });
})();
